import { useCallback, useMemo, useReducer } from "react";

// The Pricer tools that open as a modal / panel from the toolbar.
export const PRICER_TOOLS = {
  ADD_OPTION: "addOption",
  CSV_IMPORT: "csvImport",
  QUICK_ADD: "quickAdd",
  TRADE_CAPTURE: "tradeCapture",
  VOL_COMPARISON: "volComparison",
};

const OPEN = "OPEN";
const CLOSE = "CLOSE";

export const initialPricerToolsState = {
  open: Object.fromEntries(
    Object.values(PRICER_TOOLS).map((tool) => [tool, false])
  ),
  // Bumped on every Quick Add open so the input refocuses even when the bar
  // is already open.
  quickAddFocusKey: 0,
};

export const pricerToolsReducer = (state, action) => {
  switch (action.type) {
    case OPEN:
      return {
        ...state,
        open: { ...state.open, [action.tool]: true },
        quickAddFocusKey:
          action.tool === PRICER_TOOLS.QUICK_ADD
            ? state.quickAddFocusKey + 1
            : state.quickAddFocusKey,
      };
    case CLOSE:
      if (!state.open[action.tool]) return state;
      return { ...state, open: { ...state.open, [action.tool]: false } };
    default:
      return state;
  }
};

export const openTool = (tool) => ({ type: OPEN, tool });
export const closeTool = (tool) => ({ type: CLOSE, tool });

/**
 * Open/close state for every Pricer toolbar tool.
 *
 * Returns `isOpen` (by tool), `quickAddFocusKey`, stable `open(tool)` and
 * `close(tool)`, and `setters[tool]` — a stable `(open:boolean) => void` for
 * modals whose onClose prop expects a useState setter.
 */
const usePricerTools = () => {
  const [state, dispatch] = useReducer(
    pricerToolsReducer,
    initialPricerToolsState
  );

  const open = useCallback((tool) => dispatch(openTool(tool)), []);
  const close = useCallback((tool) => dispatch(closeTool(tool)), []);

  const setters = useMemo(
    () =>
      Object.fromEntries(
        Object.values(PRICER_TOOLS).map((tool) => [
          tool,
          (isOpen) => dispatch(isOpen ? openTool(tool) : closeTool(tool)),
        ])
      ),
    []
  );

  return {
    isOpen: state.open,
    quickAddFocusKey: state.quickAddFocusKey,
    open,
    close,
    setters,
  };
};

export default usePricerTools;
